import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Star, Bell } from "lucide-react";
import { tickColor, confidenceColor } from "@/lib/signalColors";

export function AssetCard({ asset, isWatched, tick, onToggleWatchlist, onNavigate, onSetAlert }) {
  return (
    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
      <Card
        data-testid={`asset-card-${asset.symbol}`}
        className="glass-effect cursor-pointer hover:border-[#00E0FF]/30 transition-all duration-300"
        onClick={() => onNavigate(`/asset/${asset.symbol}`)}
      >
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl sm:text-2xl font-bold text-white mono">{asset.symbol}</h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  asset.type === 'crypto' ? 'bg-[#6366F1]/20 text-[#6366F1]' : 'bg-[#00E0FF]/20 text-[#00E0FF]'
                }`}>{asset.type.toUpperCase()}</span>
              </div>
              <p className="text-xs sm:text-sm text-gray-400 truncate">{asset.name}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onSetAlert && (
                <button
                  data-testid={`watchlist-alert-${asset.symbol}`}
                  title="Set price alert"
                  onClick={(e) => { e.stopPropagation(); onSetAlert(asset); }}
                  className="p-1 rounded-full hover:bg-white/10 transition-colors"
                >
                  <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-[#F59E0B]/80 hover:text-[#F59E0B]" />
                </button>
              )}
              <button
                data-testid={`watchlist-toggle-${asset.symbol}`}
                onClick={(e) => onToggleWatchlist(asset.symbol, e)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <Star className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${
                  isWatched ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-600 hover:text-gray-400'
                }`} />
              </button>
              {asset.change_24h >= 0 ? (
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#00FFA3]" />
              ) : (
                <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF3B30]" />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-end gap-2">
              <span
                key={tick && tick !== 'none' ? `${tick}-${asset.price}` : 'price-static'}
                className={`text-2xl sm:text-3xl font-bold mono px-1 -mx-1 transition-colors duration-500 ${tickColor(tick)} ${
                  tick === 'up' ? 'price-up' : tick === 'down' ? 'price-down' : ''
                }`}
              >
                ${asset.price.toLocaleString()}
              </span>
              {tick && tick !== 'none' && (
                <span className={`text-xs font-bold animate-pulse ${tick === 'up' ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                  {tick === 'up' ? '\u25B2' : '\u25BC'}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-semibold ${asset.change_24h >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                {asset.change_24h >= 0 ? '+' : ''}{asset.change_24h.toFixed(2)}%
              </span>
              <span className="text-xs text-gray-500">24h</span>
            </div>
          </div>

          {asset.volume && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Volume</span>
                <span className="text-gray-300 mono">${(asset.volume / 1000000).toFixed(2)}M</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function SearchResultCard({ searchResult, onClose }) {
  if (!searchResult) return null;

  const { asset, prediction } = searchResult;

  const PredictionBlock = ({ label, data }) => (
    <div className={`p-4 rounded-lg border ${
      data.prediction === 'GAIN' ? 'bg-[#00FFA3]/5 border-[#00FFA3]/20' : 'bg-[#FF3B30]/5 border-[#FF3B30]/20'
    }`}>
      <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl font-bold mono ${data.prediction === 'GAIN' ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
        {data.prediction === 'GAIN' ? '+' : '-'}{data.percentage}%
      </div>
      <div className="text-xs text-gray-400 mt-1">
        Target: <span className="text-white mono">${data.price_target?.toLocaleString()}</span>
      </div>
      <div className="text-xs text-gray-500 mt-1">
        Confidence: <span className={`font-semibold ${confidenceColor(data.confidence)}`}>{data.confidence}</span>
      </div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
      <Card className="glass-effect border border-[#00E0FF]/20 relative">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl sm:text-2xl font-bold text-white mono">{asset.symbol}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  asset.type === 'crypto' ? 'bg-[#6366F1]/20 text-[#6366F1]' : 'bg-[#00E0FF]/20 text-[#00E0FF]'
                }`}>{asset.type.toUpperCase()}</span>
              </div>
              <p className="text-sm text-gray-400">{asset.name}</p>
            </div>
            <div className="text-right">
              <div className="text-xl sm:text-2xl font-bold text-white mono">${asset.price.toLocaleString()}</div>
              <span className={`text-sm font-semibold ${asset.change_24h >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                {asset.change_24h >= 0 ? '+' : ''}{asset.change_24h}% (24h)
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <PredictionBlock label="Short-Term (1-7 days)" data={prediction.short_term} />
            <PredictionBlock label="Long-Term (1-3 months)" data={prediction.long_term} />
          </div>

          <div className="glass-effect p-3 rounded-lg mb-3">
            <p className="text-sm text-gray-300">{prediction.summary}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {prediction.key_factors?.map((factor, i) => (
              <span key={`factor-${factor}`} className="px-2 py-1 bg-white/5 rounded text-[10px] text-gray-400 border border-white/5">
                {factor}
              </span>
            ))}
            <span className={`px-2 py-1 rounded text-[10px] font-bold ${
              prediction.risk_level === 'LOW' ? 'bg-[#00FFA3]/10 text-[#00FFA3]' :
              prediction.risk_level === 'MEDIUM' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'bg-[#FF3B30]/10 text-[#FF3B30]'
            }`}>Risk: {prediction.risk_level}</span>
          </div>

          <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded hover:bg-white/10">
            <span className="text-gray-500 text-lg">&times;</span>
          </button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
